import React from "react";
import { useMutation } from "react-query";
import { Button, Modal, Stack, Toast } from "@shopify/polaris";

import api from "../../api";
import useFormatMessage from "../../hooks/useFormatMessage";
import useOpenClose from "../../hooks/useOpenClose";
import DisplayName from "../DisplayName";
import EmailView from "../EmailView";
import ErrorPanel from "../ErrorPanel";

type RemindSignerModalProps = {
  customerId: string;
  signatureId: string;
  email: string;
  name?: string;
  birthdate?: string;
  open: boolean;
  onModalClose(): void;
};

const RemindSignerModal: React.FC<RemindSignerModalProps> = ({
  customerId,
  signatureId,
  email,
  name,
  birthdate,
  open,
  onModalClose,
}) => {
  const [showSendEmailsToast, toggleSendEmailsToast, closeSendEmailsToast] = useOpenClose();

  const sendReminderMutation = useMutation(() => api.remindSigner(customerId, signatureId), {
    onSuccess: () => toggleSendEmailsToast(),
  });

  const f = useFormatMessage();

  const sendReminder = () => sendReminderMutation.mutate();

  const handleToastDismiss = () => {
    closeSendEmailsToast();
    onModalClose();
  };

  return (
    // modal should remain visible until send email success Toast is displayed
    <>
      <Modal open={open && !showSendEmailsToast} onClose={onModalClose} title={f("modals.remind.signer.title")}>
        <Modal.Section>
          <Stack vertical>
            <DisplayName type="person" name={name} birthDate={birthdate} />
            <EmailView>{email}</EmailView>

            {sendReminderMutation.isError && <ErrorPanel message={sendReminderMutation.error} />}

            <Stack distribution="trailing">
              <Button onClick={onModalClose} disabled={sendReminderMutation.isLoading}>
                {f("default.cancel")}
              </Button>
              <Button primary onClick={sendReminder} loading={sendReminderMutation.isLoading}>
                {f("modals.remind.signer.button")}
              </Button>
            </Stack>
          </Stack>
        </Modal.Section>
      </Modal>
      {showSendEmailsToast && (
        <Toast
          content={f("attachments.signatures.modal.labels.email-sent", { count: 1 })}
          onDismiss={handleToastDismiss}
          duration={4_000}
        />
      )}
    </>
  );
};

export default RemindSignerModal;
